import { useCallback, useEffect, useRef, useState } from 'react';
import { Icon } from '../components/Icons';
import { Surface } from '../components/Primitives';
import {
  backupActor,
  backupErrorMessage,
  countBackupRecords,
  createManagedBackup,
  downloadManagedBackup,
  formatBackupDate,
  formatBackupSize,
  formatBackupTrigger,
  listManagedBackups,
  startBackupDownload,
} from '../lib/backups';
import type { ManagedBackup, Tenant } from '../types';

type CustomerIdentity = Pick<Tenant, 'id' | 'name'>;

export function CustomerBackups({ customer }: { customer: CustomerIdentity }) {
  const [backups, setBackups] = useState<ManagedBackup[]>([]);
  const [loadedCustomerId, setLoadedCustomerId] = useState<string | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [listFailed, setListFailed] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [creating, setCreating] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [downloading, setDownloading] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const requestVersion = useRef(0);
  const creatingRef = useRef(false);
  const downloadingRef = useRef<Set<string>>(new Set());

  const load = useCallback(async (cursor?: string) => {
    const version = requestVersion.current;
    cursor ? setLoadingMore(true) : setLoading(true);
    if (!cursor) { setError(null); setListFailed(false); }
    try {
      const result = await listManagedBackups(customer.id, cursor);
      if (version !== requestVersion.current) return;
      setBackups((current) => cursor
        ? [...current, ...result.backups.filter((item) => !current.some((existing) => existing.backupId === item.backupId))]
        : result.backups);
      setLoadedCustomerId(customer.id);
      setNextCursor(result.nextCursor);
    } catch (loadError) {
      if (version === requestVersion.current) {
        setLoadedCustomerId(customer.id);
        setListFailed(true);
        setError(backupErrorMessage(loadError, 'list'));
      }
    } finally {
      if (version === requestVersion.current) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  }, [customer.id]);

  useEffect(() => {
    requestVersion.current += 1;
    setBackups([]);
    setLoadedCustomerId(null);
    setNextCursor(null);
    setListFailed(false);
    downloadingRef.current.clear();
    setDownloading(new Set());
    setError(null);
    setSuccess(null);
    setConfirming(false);
    void load();
    return () => { requestVersion.current += 1; };
  }, [customer.id, load]);

  const create = async () => {
    if (creatingRef.current) return;
    creatingRef.current = true;
    setCreating(true);
    setConfirming(false);
    setError(null);
    setSuccess(null);
    const version = requestVersion.current;
    try {
      const result = await createManagedBackup(customer.id);
      if (version !== requestVersion.current) return;
      startBackupDownload(result);
      setSuccess('Backup created. Your download is ready.');
      await load();
    } catch (createError) {
      if (version === requestVersion.current) setError(backupErrorMessage(createError, 'create'));
    } finally {
      creatingRef.current = false;
      if (version === requestVersion.current) setCreating(false);
    }
  };

  const download = async (backup: ManagedBackup) => {
    if (downloadingRef.current.has(backup.backupId)) return;
    downloadingRef.current.add(backup.backupId);
    setDownloading((current) => new Set(current).add(backup.backupId));
    setError(null);
    const version = requestVersion.current;
    try {
      const result = await downloadManagedBackup(customer.id, backup.backupId);
      if (version === requestVersion.current) startBackupDownload(result);
    } catch (downloadError) {
      if (version === requestVersion.current) setError(backupErrorMessage(downloadError, 'download'));
    } finally {
      downloadingRef.current.delete(backup.backupId);
      if (version === requestVersion.current) setDownloading((current) => {
        const next = new Set(current); next.delete(backup.backupId); return next;
      });
    }
  };

  const visibleBackups = loadedCustomerId === customer.id ? backups : [];
  const customerLoading = loading || loadedCustomerId !== customer.id;
  const latest = visibleBackups[0];
  return <div className="backups-workspace">
    <Surface
      title="Backups"
      description={`Encrypted exports of key Compass customer data for ${customer.name}.`}
      action={<button className="primary compact-button" disabled={creating} onClick={() => setConfirming(true)}>{creating ? 'Creating…' : 'Create Backup'}</button>}
    >
      {confirming && <div className="backup-confirm" role="dialog" aria-modal="true" aria-labelledby="backup-confirm-title">
        <div><strong id="backup-confirm-title">Create a new backup for {customer.name}?</strong><p>The encrypted backup will be stored and downloaded when it is ready.</p></div>
        <div><button className="secondary compact-button" onClick={() => setConfirming(false)}>Cancel</button><button className="primary compact-button" disabled={creating} onClick={() => void create()}>{creating ? 'Creating…' : 'Create Backup'}</button></div>
      </div>}
      {success && <div className="feedback success" role="status"><Icon name="shield" />{success}</div>}
      {error && <div className="feedback error" role="alert">{error}<button className="text-link" onClick={() => setError(null)}>Dismiss</button></div>}
      {!loading && latest && <div className="backup-latest" aria-label="Latest backup summary">
        <div><span>Latest backup</span><strong>{formatBackupDate(latest.createdAt)}</strong></div>
        <div><span>Created by</span><strong>{backupActor(latest.trigger, latest.createdBy)}</strong><small>{formatBackupTrigger(latest.trigger)}</small></div>
        <div><span>Contents</span><strong>{countBackupRecords(latest.docCounts).toLocaleString()} records</strong><small>{latest.collections.length} collections</small></div>
        <div><span>Size</span><strong>{formatBackupSize(latest.bytes)}</strong></div>
      </div>}
      {customerLoading ? <div className="backup-loading"><span className="loader"/> Loading backups for {customer.name}</div> : listFailed && visibleBackups.length === 0 ? <div className="backup-empty"><span className="empty-icon"><Icon name="backup" /></span><h2>Backups are unavailable</h2><p>Try loading this customer’s backup history again.</p><button className="secondary compact-button" onClick={() => void load()}>Try again</button></div> : visibleBackups.length === 0 ? <div className="backup-empty"><span className="empty-icon"><Icon name="backup" /></span><h2>No backups yet</h2><p>Create the first encrypted export for {customer.name} when one is needed.</p><button className="primary compact-button" onClick={() => setConfirming(true)}>Create Backup</button></div> : <>
        <div className="backup-table-wrap"><div className="backup-table" role="table" aria-label={`${customer.name} backups`}>
          <div className="backup-table-head" role="row"><span>Created</span><span>Type</span><span>Created by</span><span>Contents</span><span>Size</span><span>Download</span></div>
          {visibleBackups.map((backup) => <div className="backup-table-row" role="row" key={backup.backupId}>
            <span className="backup-created" data-label="Created">{formatBackupDate(backup.createdAt)}</span>
            <span data-label="Type"><span className={`backup-type ${backup.trigger}`}>{formatBackupTrigger(backup.trigger)}</span></span>
            <span className="backup-actor" data-label="Created by">{backupActor(backup.trigger, backup.createdBy)}</span>
            <span data-label="Contents"><details className="backup-contents"><summary>{countBackupRecords(backup.docCounts).toLocaleString()} records</summary><div>{Object.entries(backup.docCounts).sort(([a], [b]) => a.localeCompare(b)).map(([name, count]) => <span key={name}><b>{name}</b>{count.toLocaleString()}</span>)}</div></details></span>
            <span data-label="Size">{formatBackupSize(backup.bytes)}</span>
            <span data-label="Download"><button className="download-button" disabled={downloading.has(backup.backupId)} onClick={() => void download(backup)}><Icon name="download" />{downloading.has(backup.backupId) ? 'Downloading…' : 'Download'}</button></span>
          </div>)}
        </div></div>
        {nextCursor && <div className="backup-pagination"><button className="secondary compact-button" disabled={loadingMore} onClick={() => void load(nextCursor)}>{loadingMore ? 'Loading…' : 'Load more'}</button></div>}
      </>}
    </Surface>
    <p className="workspace-footnote">Scheduled backups are created automatically. Exports cover key customer data, not authentication records or every Compass dataset.</p>
  </div>;
}
